import { sendGTMEvent } from "@next/third-parties/google"
import { Onfido } from "onfido-sdk-ui"
import React, { useEffect, useRef, useState } from "react"

import MedusaApiClient from "@lib/clients/medusa-api-client"
import NeloApiClient from "@lib/clients/nelo-api-client"
import { neloApiClient } from "@lib/config-client"
import { sendGoogleAnalyticsEvent } from "@modules/common/utils/analytics"
import LoadingScreen from "@modules/signup-and-order-flow/components/loading"

const OnfidoVerification = (props: { authToken: string; onComplete: () => void; useOnfidoLink?: boolean }) => {
  const onfidoMountRef = useRef<HTMLDivElement>(null)
  const [sdkToken, setSdkToken] = useState<string | null>(null)
  const [isLoadingToken, setIsLoadingToken] = useState(false)
  const onfidoInstanceRef = useRef<any>(null)

  useEffect(() => {
    const fetchApplicant = async () => {
      setIsLoadingToken(true)
      NeloApiClient.setToken(props.authToken)
      MedusaApiClient.setToken(props.authToken)
      try {
        const data = props.useOnfidoLink ? await neloApiClient.createOnfidoLinkApplicant() : await neloApiClient.createOnfidoApplicant()
        if (data.sdk_token) {
          setSdkToken(data.sdk_token)
        } else {
          throw new Error("sdktoken not found")
        }
      } catch (err) {
        console.error("sdktoken fetch error", err)
      } finally {
        setIsLoadingToken(false)
      }
    }

    fetchApplicant()
  }, [props.authToken, props.useOnfidoLink])

  useEffect(() => {
    const neloBlue = "#067Bf2"
    const darkBlue = "#02305F"
    const black = "#444444"
    const white = "#FFFFFF"
    const transparent = "transparent"

    if (onfidoMountRef.current && sdkToken) {
      const instance = Onfido.init({
        customUI: {
          borderRadiusButton: "100px",
          borderStyleSurfaceModal: "0px",
          fontWeightBody: 400,
          fontSizeBody: "16px",
          fontSizeSubtitle: "16px",
          fontSizeTitle: "40px",
          colorBackgroundSurfaceModal: transparent,
          colorBackgroundIcon: transparent,
          colorBorderLinkUnderline: transparent,
          colorBackgroundLinkActive: transparent,
          colorBackgroundLinkHover: transparent,
          colorBackgroundDocTypeButton: transparent,
          colorContentBody: black,
          colorContentTitle: neloBlue,
          colorContentSubtitle: black,
          colorContentButtonPrimaryText: white,
          colorBackgroundButtonPrimary: neloBlue,
          colorBackgroundButtonPrimaryHover: darkBlue,
          colorBackgroundButtonPrimaryActive: neloBlue,
          colorContentButtonSecondaryText: white,
          colorBackgroundButtonSecondary: neloBlue,
          colorBackgroundButtonSecondaryHover: neloBlue,
          colorBackgroundButtonSecondaryActive: neloBlue,
          colorContentLinkTextHover: neloBlue,
          colorBorderDocTypeButton: black,
          colorBackgroundDocTypeButtonHover: darkBlue,
          colorBackgroundDocTypeButtonActive: darkBlue,
          colorContentDocTypeButton: white,
          colorIcon: neloBlue,
          colorContentInfoPill: black,
          colorBackgroundInfoPill: neloBlue,
          colorBackgroundSelector: neloBlue,
          colorBackgroundDropdownItemHover: darkBlue,
          colorInputOutline: neloBlue,
          colorContentButtonTertiaryText: black,
          colorContentInput: neloBlue,
        },
        token: sdkToken,
        containerEl: onfidoMountRef.current,
        steps: [
          {
            type: "welcome",
            options: {
              title: "Verifica tu identidad",
            },
          },
          {
            type: "document",
            options: {
              clipboardWrite: false,
              genericDocumentTypes: [
                {
                  id: "national_identity_card",
                  title: "National ID Card",
                  country: "MEX",
                  pages: 2,
                },
              ],
              documentTypes: {
                generic_document: {
                  id: "national_identity_card",
                  country: "MEX",
                },
              },
            },
          },
          {
            type: "face",
            options: {
              requestedVariant: "motion",
            },
          },
        ],
        onComplete: function () {
          if (props.useOnfidoLink) {
            neloApiClient.triggerOnfidoLinkCheck()
          } else {
            neloApiClient.triggerOnfidoCheck()
          }
          props.onComplete()
          sendGTMEvent({
            event: "onfido_verification_complete",
          })
          sendGoogleAnalyticsEvent("ONFIDO_VERIFICATION_COMPLETE")
        },
        onError: function (error) {
          sendGTMEvent({
            event: "onfido_verification_error",
          })
          sendGoogleAnalyticsEvent("ONFIDO_VERIFICATION_ERROR")
        },
      })
      onfidoInstanceRef.current = instance
    }

    return () => {
      if (onfidoInstanceRef.current) {
        onfidoInstanceRef.current.tearDown()
        onfidoInstanceRef.current = null
      }
    }
  }, [sdkToken, onfidoMountRef, props.useOnfidoLink])

  return (
    <div className="min-h-full overflow-auto">
      {isLoadingToken && (
        <div>
          <LoadingScreen />
        </div>
      )}
      <div className="mb-[30%]">
        <div ref={onfidoMountRef} />
      </div>
    </div>
  )
}

export default OnfidoVerification
